
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'reference',
  source: 'endpoint',
  secondLevelToc: true,
  alwaysExpand: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'API reference for Users'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='User endpoints' subtitle='API reference for user management endpoints' mdxType="Header" />
    <p>{`This page covers API endpoints related to user ids and user tokens, which are used by `}<a parentName="p" {...{
        "href": "/docs/check"
      }}>{`Plaid Check`}</a>{`, Plaid Protect, `}<a parentName="p" {...{
        "href": "/docs/income"
      }}>{`Income`}</a>{`, and `}<a parentName="p" {...{
        "href": "/docs/payment-initiation"
      }}>{`Payment Initiation`}</a>{` (including Variable Recurring Payments and Virtual Accounts), as well as by the `}<a parentName="p" {...{
        "href": "/docs/link/multi-item-link"
      }}>{`Multi-Item Link flow`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Guides`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/api/users/user-apis"
            }}>{`New User APIs`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Overview of changes introduced in the new User APIs`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/api/users/migrate-to-new-user-apis"
            }}>{`Migrate to new User APIs`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Migration guide for existing Consumer Report and Income integrations on legacy User APIs`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/user/create`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create a user ID`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/user/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get user details`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/user/update`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Update user data or enable an existing user for Plaid Check`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/user/items/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Return Items associated with a user`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/user/items/associate`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Associate Items with a user`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/user/items/remove`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Remove Items associated with a User`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/user/products/terminate`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Terminate user-based products`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`See also`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/sandbox/user/reset_login`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Force user into an error state for testing`}</td>
        </tr>
      </tbody>
    </table>
    <Callout warning mdxType="Callout">
      <p>{`Plaid introduced `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`-based APIs for new Plaid Check and Multi-Item Link integrations on December 10, 2025, and for new Income integrations on August 17, 2026. If you have an existing integration that uses `}<inlineCode parentName="p">{`user_token`}</inlineCode>{`, you can continue to use it. Plaid Check, Multi-Item Link, and Income customers can also optionally `}<a parentName="p" {...{
          "href": "/docs/api/users/migrate-to-new-user-apis"
        }}>{`migrate to the new User APIs`}</a>{` to begin using `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`. New customers must use `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`.`}</p>
      <p>{`To accommodate both flows, many Plaid API endpoints use either a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` or a `}<inlineCode parentName="p">{`user_token`}</inlineCode>{`. In these API requests, you should provide a `}<inlineCode parentName="p">{`user_token`}</inlineCode>{` only if you have one; otherwise, provide a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`. You do not need to provide both fields.`}</p>
      <p>{`For more details on this change, see `}<a parentName="p" {...{
          "href": "/docs/api/users/user-apis"
        }}>{`New User APIs`}</a>{`.`}</p>
    </Callout>
    <h4 {...{
      "id": "user-identifiers"
    }}>{`User identifiers`}</h4>
    <p>{`There are three identifiers Plaid uses when working with users:`}</p>
    <p><inlineCode parentName="p">{`client_user_id`}</inlineCode>{`: The unique identifier you provide to Plaid for each end user in your application. This is determined by you and will typically correspond to your application's primary key for a user record. It must not contain PII, such as a phone number, Social Security number, or email address.`}</p>
    <p><inlineCode parentName="p">{`user_id`}</inlineCode>{`: Plaid-generated identifier, prefixed by `}<inlineCode parentName="p">{`usr_`}</inlineCode>{` and returned in responses from the User APIs and `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. Each `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` corresponds to a single `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{`; calling `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` repeatedly with the same `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` returns the same `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`. The `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` is used by new Plaid Check and Multi-Item Link customers who integrated on or after December 10, 2025, and new Income customers who integrated on or after August 17, 2026.`}</p>
    <p><inlineCode parentName="p">{`user_token`}</inlineCode>{` — A Plaid-generated token for accessing the user. Applicable to Plaid Check and Multi-Item Link customers who integrated with `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` before December 10, 2025, and Income customers who integrated before August 17, 2026. Note that `}<inlineCode parentName="p">{`user_tokens`}</inlineCode>{` can also have corresponding legacy `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`s; these are not equivalent to the new `}<inlineCode parentName="p">{`usr_*`}</inlineCode>{`-prefixed `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`s mentioned above. For more details, see `}<a parentName="p" {...{
        "href": "/docs/api/users/user-apis"
      }}>{`New User APIs`}</a>{`.`}</p>
    <h4 {...{
      "id": "usercreate"
    }}>{`/user/create`}</h4>
    <Schema method='POST' route='/user/create' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/user/create' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/user/create \\
  -H 'Content-Type: application/json' \\
  -d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "client_user_id": "c0e2c4ee-b763-4af5-cfe9-46a46bce883d",
  "identity": {
    "name": {
      "given_name": "Carmen",
      "family_name": "Berzatto"
    },
    "date_of_birth": "1987-01-31",
    "emails": [
      { "data": "carmen@example.com", "primary": true }
    ],
    "phone_numbers": [
      { "data": "+13125551212", "primary": true },
      { "data": "+12125551212", "primary": false }
    ],
    "addresses": [
      {
        "street_1": "3200 W Armitage Ave",
        "street_2": null,
        "city": "Chicago",
        "region": "IL",
        "country": "US",
        "postal_code": "60657",
        "primary": true
      }
    ],
    "id_numbers": [
      {
        "value": "1234",
        "type": "us_ssn_last_4"
      }
    ]
  }
  }'

`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request = {
  client_user_id: "c0e2c4ee-b763-4af5-cfe9-46a46bce883d",
  identity: {
    name: {
      given_name: "Carmen",
      family_name: "Berzatto"
    },
    date_of_birth: "1987-01-31",
    emails: [
      { data: "carmy@example.com", primary: true },
      { data: "bear@example.com", primary: false }
    ],
    phone_numbers: [
      { data: "+13125551212", primary: true }
    ],
    addresses: [
      {
        street_1: "3200 W Armitage Ave",
        street_2: null,
        city: "Chicago",
        region: "IL",
        country: "US",
        postal_code: "60657",
        primary: true
      }
    ],
    id_numbers: [
      {
        value: "1234",
        type: "us_ssn_last_4"
      }
    ]
  }
};

try {
  const response = await client.userCreate(request);
} catch (error) {
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = UserCreateRequest(
    client_user_id="c0e2c4ee-b763-4af5-cfe9-46a46bce883d",
    identity=ClientUserIdentity(
        name=ClientUserIdentityName(
            given_name="Carmen",
            family_name="Berzatto"
        ),
        date_of_birth=date(1987, 1, 31),
        emails=[
            ClientUserIdentityEmail(data="carmy@example.com", primary=True),
            ClientUserIdentityEmail(data="bear@example.com", primary=False)
        ],
        phone_numbers=[
            ClientUserIdentityPhoneNumber(data="+13125551212", primary=True)
        ],
        addresses=[
            ClientUserIdentityAddress(
                street_1="3200 W Armitage Ave",
                street_2=None,
                city="Chicago",
                region="IL",
                country="US",
                postal_code="60657",
                primary=True
            )
        ],
        id_numbers=[
            UserIDNumber(
                value="1234",
                type=IDNumberType("us_ssn_last_4")
            )
        ]
    )
)

response = client.user_create(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::UserCreateRequest.new(
  client_user_id: "c0e2c4ee-b763-4af5-cfe9-46a46bce883d",
  identity: {
    name: {
      given_name: "Carmen",
      family_name: "Berzatto"
    },
    date_of_birth: "1987-01-31",
    emails: [
      { data: "carmy@example.com", primary: true },
      { data: "bear@example.com", primary: false }
    ],
    phone_numbers: [
      { data: "+13125551212", primary: true }
    ],
    addresses: [
      {
        street_1: "3200 W Armitage Ave",
        street_2: nil,
        city: "Chicago",
        region: "IL",
        country: "US",
        postal_code: "60657",
        primary: true
      }
    ],
    id_numbers: [
      {
        value: "1234",
        type: "us_ssn_last_4"
      }
    ]
  }
)

response = client.user_create(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`
ClientUserIdentityAddress addressData = new ClientUserIdentityAddress()
  .street1("3200 W Armitage Ave")
  .street2(null)
  .city("Chicago")
  .region("IL")
  .country("US")
  .postalCode("60657")
  .primary(true);

ClientUserIdentityName name = new ClientUserIdentityName()
  .givenName("Carmen")
  .familyName("Berzatto");

ClientUserIdentityEmail email1 = new ClientUserIdentityEmail()
  .data("carmy@example.com")
  .primary(true);

ClientUserIdentityEmail email2 = new ClientUserIdentityEmail()
  .data("bear@example.com")
  .primary(false);

ClientUserIdentityPhoneNumber phone1 = new ClientUserIdentityPhoneNumber()
  .data("+13125551212")
  .primary(true);

UserIDNumber idNumber = new UserIDNumber()
  .value("1234")
  .type(IDNumberType.US_SSN_LAST_4);

ClientUserIdentity clientUserIdentity = new ClientUserIdentity()
  .name(name)
  .dateOfBirth(LocalDate.parse("1987-01-31"))
  .emails(Arrays.asList(email1, email2))
  .phoneNumbers(Arrays.asList(phone1))
  .addresses(Arrays.asList(addressData))
  .idNumbers(Arrays.asList(idNumber));

UserCreateRequest request = new UserCreateRequest()
  .clientUserId("c0e2c4ee-b763-4af5-cfe9-46a46bce883d")
  .identity(clientUserIdentity);

Response<UserCreateResponse> response = client
  .userCreate(request, null)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`city := "Chicago"
region := "IL"
street := "3200 W Armitage Ave"
postalCode := "60657"
country := "US"

DateOfBirth := "1987-01-31"
emails := []string{"carmy@example.com", "bear@example.com"}
phoneNumbers := []string{"+13125551212"}

firstName := "Carmen"
lastName := "Berzatto"
ssnLast4 := "1234"

addressData := plaid.NewClientUserIdentityAddress(country, true)
addressData.SetStreet1(street)
addressData.SetCity(city)
addressData.SetRegion(region)
addressData.SetPostalCode(postalCode)

name := plaid.ClientUserIdentityName{
    GivenName:  firstName,
    FamilyName: lastName,
}

clientUserEmails := []plaid.ClientUserIdentityEmail{}
for i, e := range emails {
    clientUserEmails = append(clientUserEmails, plaid.ClientUserIdentityEmail{
        Data:    e,
        Primary: i == 0,
    })
}

clientUserPhones := []plaid.ClientUserIdentityPhoneNumber{}
for i, p := range phoneNumbers {
    clientUserPhones = append(clientUserPhones, plaid.ClientUserIdentityPhoneNumber{
        Data:    p,
        Primary: i == 0,
    })
}

idNumber := plaid.UserIDNumber{
    Value: ssnLast4,
    Type:  "us_ssn_last_4",
}

clientUserIdentity := plaid.ClientUserIdentity{
    Name:        *plaid.NewNullableClientUserIdentityName(&name),
    DateOfBirth: *plaid.NewNullableString(&DateOfBirth),
    Emails:      &clientUserEmails,
    PhoneNumbers: &clientUserPhones,
    Addresses:   &[]plaid.ClientUserIdentityAddress{*addressData},
    IdNumbers:   &[]plaid.UserIDNumber{idNumber},
}

request := plaid.NewUserCreateRequest(
    "c0e2c4ee-b763-4af5-cfe9-46a46bce883d",
)

request.SetIdentity(clientUserIdentity)

response, _, err := client.PlaidApi.UserCreate(ctx).UserCreateRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`city := "Chicago"
region := "IL"
street := "3200 W Armitage Ave"
postalCode := "60657"
country := "US"

DateOfBirth := time.Date(1987, 1, 31, 0, 0, 0, 0, time.UTC)
emails := []string{"carmy@example.com", "bear@example.com"}
phoneNumbers := []string{"+13125551212"}

firstName := "Carmen"
lastName := "Berzatto"
ssnLast4 := "1234"

addressData := &plaid.ClientUserIdentityAddress{
    Country: country,
    Primary: true,
}
addressData.Street1 = &street
addressData.City = &city
addressData.Region = &region
addressData.PostalCode = &postalCode

name := plaid.ClientUserIdentityName{
    GivenName:  firstName,
    FamilyName: lastName,
}

clientUserEmails := []*plaid.ClientUserIdentityEmail{}
for i, e := range emails {
    clientUserEmails = append(clientUserEmails, &plaid.ClientUserIdentityEmail{
        Data:    e,
        Primary: i == 0,
    })
}

clientUserPhones := []*plaid.ClientUserIdentityPhoneNumber{}
for i, p := range phoneNumbers {
    clientUserPhones = append(clientUserPhones, &plaid.ClientUserIdentityPhoneNumber{
        Data:    p,
        Primary: i == 0,
    })
}

idNumber := plaid.UserIDNumber{
    Value: ssnLast4,
    Type:  "us_ssn_last_4",
}

clientUserIdentity := plaid.ClientUserIdentity{
    Name:         &name,
    DateOfBirth:  &DateOfBirth,
    Emails:       clientUserEmails,
    PhoneNumbers: clientUserPhones,
    Addresses:    []*plaid.ClientUserIdentityAddress{addressData},
    IDNumbers:    []*plaid.UserIDNumber{&idNumber},
}

request := &plaid.UserCreateRequest{ClientUserID: "c0e2c4ee-b763-4af5-cfe9-46a46bce883d"}

request.Identity = &clientUserIdentity

response, err := client.User.Create(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/user/create' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "userget"
    }}>{`/user/get`}</h4>
    <Schema method='POST' route='/user/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/user/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/user/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user_id": "usr_9nSp2KuZ2x4JDw"
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request = {
  user_id: "usr_9nSp2KuZ2x4JDw"
};

try {
  const response = await client.userGet(request);
} catch (error) {
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = UserGetRequest(
  user_id="usr_9nSp2KuZ2x4JDw",
)

response = client.user_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::UserGetRequest.new(
  {
    user_id: "usr_9nSp2KuZ2x4JDw",
  }
)

response = client.user_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`UserGetRequest request = new UserGetRequest()
  .userId("usr_9nSp2KuZ2x4JDw");

Response<UserGetResponse> response = client
  .userGet(request, null)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.UserGetRequest{
  UserId: "usr_9nSp2KuZ2x4JDw",
}

response, _, err := client.PlaidApi.UserGet(ctx).UserGetRequest(request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.UserGetRequest{UserID: "usr_9nSp2KuZ2x4JDw"}

response, err := client.User.Get(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/user/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "userupdate"
    }}>{`/user/update`}</h4>
    <Schema method='POST' route='/user/update' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/user/update' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/user/update \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user_id": "usr_9nSp2KuZ2x4JDw",
  "identity": {
    "name": {
      "given_name": "Carmen",
      "family_name": "Berzatto"
    },
    "date_of_birth": "1987-01-31",
    "emails": [
      { "data": "carmen@example.com", "primary": true }
    ],
    "phone_numbers": [
      { "data": "+13125551212", "primary": true },
      { "data": "+12125551212", "primary": false }
    ],
    "addresses": [
      {
        "street_1": "3200 W Armitage Ave",
        "street_2": null,
        "city": "Chicago",
        "region": "IL",
        "country": "US",
        "postal_code": "60657",
        "primary": true
      }
    ],
    "id_numbers": [
      {
        "value": "1234",
        "type": "us_ssn_last_4"
      }
    ]
  }
}'

`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request = {
  user_id: 'usr_9nSp2KuZ2x4JDw',
  identity: {
    name: {
      given_name: "Carmen",
      family_name: "Berzatto"
    },
    date_of_birth: "1987-01-31",
    emails: [
      { data: "carmy@example.com", primary: true },
      { data: "bear@example.com", primary: false }
    ],
    phone_numbers: [
      { data: "+13125551212", primary: true }
    ],
    addresses: [
      {
        street_1: "3200 W Armitage Ave",
        street_2: null,
        city: "Chicago",
        region: "IL",
        country: "US",
        postal_code: "60657",
        primary: true
      }
    ],
    id_numbers: [
      {
        value: "1234",
        type: "us_ssn_last_4"
      }
    ]
  }
};

try {
  const response = await client.userUpdate(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = UserUpdateRequest(
  user_id='usr_9nSp2KuZ2x4JDw',
  identity=ClientUserIdentity(
    name=ClientUserIdentityName(
      given_name='Carmen',
      family_name='Berzatto'
    ),
    date_of_birth=date(1987, 1, 31),
    emails=[
      ClientUserIdentityEmail(data='carmy@example.com', primary=True),
      ClientUserIdentityEmail(data='bear@example.com', primary=False),
    ],
    phone_numbers=[
      ClientUserIdentityPhoneNumber(data='+13125551212', primary=True),
    ],
    addresses=[
      ClientUserIdentityAddress(
        street_1='3200 W Armitage Ave',
        street_2=None,
        city='Chicago',
        region='IL',
        country='US',
        postal_code='60657',
        primary=True,
      )
    ],
    id_numbers=[
      UserIDNumber(
        value='1234',
        type=IDNumberType('us_ssn_last_4'),
      )
    ]
  )
)

response = client.user_update(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::UserUpdateRequest.new(
  {
    user_id: 'usr_9nSp2KuZ2x4JDw',
    identity: {
      name: {
        given_name: "Carmen",
        family_name: "Berzatto"
      },
      date_of_birth: "1987-01-31",
      emails: [
        { data: "carmy@example.com", primary: true },
        { data: "bear@example.com", primary: false }
      ],
      phone_numbers: [
        { data: "+13125551212", primary: true }
      ],
      addresses: [
        {
          street_1: "3200 W Armitage Ave",
          street_2: nil,
          city: "Chicago",
          region: "IL",
          country: "US",
          postal_code: "60657",
          primary: true
        }
      ],
      id_numbers: [
        {
          value: "1234",
          type: "us_ssn_last_4"
        }
      ]
    }
  }
)

response = client.user_update(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`
ClientUserIdentityAddress addressData = new ClientUserIdentityAddress().
  street1("3200 W Armitage Ave").
  street2(null).
  city("Chicago").
  region("IL").
  country("US").
  postalCode("60657").
  primary(true);

ClientUserIdentityName name = new ClientUserIdentityName().
  givenName("Carmen").
  familyName("Berzatto");

ClientUserIdentityEmail email1 = new ClientUserIdentityEmail().
  data("carmy@example.com").
  primary(true);

ClientUserIdentityEmail email2 = new ClientUserIdentityEmail().
  data("bear@example.com").
  primary(false);

ClientUserIdentityPhoneNumber phone1 = new ClientUserIdentityPhoneNumber().
  data("+13125551212").
  primary(true);

UserIDNumber idNumber = new UserIDNumber().
  value("1234").
  type(IDNumberType.US_SSN_LAST_4);

ClientUserIdentity clientUserIdentity = new ClientUserIdentity().
  name(name).
  dateOfBirth(LocalDate.parse("1987-01-31")).
  emails(Arrays.asList(email1, email2)).
  phoneNumbers(Arrays.asList(phone1)).
  addresses(Arrays.asList(addressData)).
  idNumbers(Arrays.asList(idNumber));

UserUpdateRequest request = new UserUpdateRequest()
  .userId("usr_9nSp2KuZ2x4JDw")
  .identity(clientUserIdentity);

Response<UserUpdateResponse> response = client
  .userUpdate(request, null)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`city := "Chicago"
region := "IL"
street := "3200 W Armitage Ave"
postalCode := "60657"
country := "US"

emails := []string{"carmy@example.com", "bear@example.com"}
phoneNumbers := []string{"+13125551212"}

addressData := plaid.NewClientUserIdentityAddress(country, true)
addressData.SetStreet1(street)
addressData.SetCity(city)
addressData.SetRegion(region)
addressData.SetPostalCode(postalCode)

name := plaid.ClientUserIdentityName{
  GivenName:  "Carmen",
  FamilyName: "Berzatto",
}

clientUserEmails := []plaid.ClientUserIdentityEmail{}
for i, e := range emails {
  clientUserEmails = append(clientUserEmails, plaid.ClientUserIdentityEmail{
    Data:    e,
    Primary: i == 0,
  })
}

clientUserPhones := []plaid.ClientUserIdentityPhoneNumber{}
for i, p := range phoneNumbers {
  clientUserPhones = append(clientUserPhones, plaid.ClientUserIdentityPhoneNumber{
    Data:    p,
    Primary: i == 0,
  })
}

idNumber := plaid.UserIDNumber{
  Value: "1234",
  Type:  "us_ssn_last_4",
}

clientUserIdentity := plaid.ClientUserIdentity{
  Name:        *plaid.NewNullableClientUserIdentityName(&name),
  DateOfBirth: *plaid.NewNullableString(plaid.PtrString("1987-01-31")),
  Emails:      &clientUserEmails,
  PhoneNumbers: &clientUserPhones,
  Addresses:   &[]plaid.ClientUserIdentityAddress{*addressData},
  IdNumbers:   &[]plaid.UserIDNumber{idNumber},
}

request := plaid.NewUserUpdateRequest()
request.SetUserId("usr_9nSp2KuZ2x4JDw")
request.SetIdentity(clientUserIdentity)

response, _, err := client.PlaidApi.UserUpdate(ctx).UserUpdateRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`city := "Chicago"
region := "IL"
street := "3200 W Armitage Ave"
postalCode := "60657"
country := "US"

emails := []string{"carmy@example.com", "bear@example.com"}
phoneNumbers := []string{"+13125551212"}

addressData := &plaid.ClientUserIdentityAddress{
    Country: country,
    Primary: true,
}
addressData.Street1 = &street
addressData.City = &city
addressData.Region = &region
addressData.PostalCode = &postalCode

name := plaid.ClientUserIdentityName{
    GivenName:  "Carmen",
    FamilyName: "Berzatto",
}

clientUserEmails := []*plaid.ClientUserIdentityEmail{}
for i, e := range emails {
    clientUserEmails = append(clientUserEmails, &plaid.ClientUserIdentityEmail{
        Data:    e,
        Primary: i == 0,
    })
}

clientUserPhones := []*plaid.ClientUserIdentityPhoneNumber{}
for i, p := range phoneNumbers {
    clientUserPhones = append(clientUserPhones, &plaid.ClientUserIdentityPhoneNumber{
        Data:    p,
        Primary: i == 0,
    })
}

idNumber := plaid.UserIDNumber{
    Value: "1234",
    Type:  "us_ssn_last_4",
}

clientUserIdentity := plaid.ClientUserIdentity{
    Name:         &name,
    DateOfBirth:  plaid.Time(time.Date(1987, 1, 31, 0, 0, 0, 0, time.UTC)),
    Emails:       clientUserEmails,
    PhoneNumbers: clientUserPhones,
    Addresses:    []*plaid.ClientUserIdentityAddress{addressData},
    IDNumbers:    []*plaid.UserIDNumber{&idNumber},
}

request := &plaid.UserUpdateRequest{}
request.UserID = plaid.String("usr_9nSp2KuZ2x4JDw")
request.Identity = &clientUserIdentity

response, err := client.User.Update(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/user/update' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "useritemsget"
    }}>{`/user/items/get`}</h4>
    <Schema method='POST' route='/user/items/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/user/items/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/user/items/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user_id": "usr_9nSp2KuZ2x4JDw"  
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request = {
  user_id: 'usr_9nSp2KuZ2x4JDw'
};

try {
  const response = await client.userItemsGet(request);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = UserItemsGetRequest(
  user_id='usr_9nSp2KuZ2x4JDw',
)

response = client.user_items_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::UserItemsGetRequest.new(
  {
    user_id: 'usr_9nSp2KuZ2x4JDw',
  }
)

response = client.user_items_get(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`
UserItemsGetRequest request = new UserItemsGetRequest()
  .userId("usr_9nSp2KuZ2x4JDw");

Response<UserItemsGetResponse> response = client
  .userItemsGet(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewUserItemsGetRequest()
request.SetUserId("usr_9nSp2KuZ2x4JDw")

response, _, err := client.PlaidApi.UserItemsGet(ctx).UserItemsGetRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.UserItemsGetRequest{}
request.UserID = plaid.String("usr_9nSp2KuZ2x4JDw")

response, err := client.User.GetItems(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/user/items/get' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "useritemsassociate"
    }}>{`/user/items/associate`}</h4>
    <Callout mdxType="Callout">
      <p>{`This endpoint is currently in early availability. To request access, contact your account manager or `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/admin/account-administration/request-product-access"
        }}>{`submit a product access support ticket`}</a>{`.`}</p>
    </Callout>
    <Schema method='POST' route='/user/items/associate' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/user/items/associate' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/user/items/associate \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user_id": "usr_9nSp2KuZ2x4JDw",
  "item_ids": ["eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1"]
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request = {
  user_id: 'usr_9nSp2KuZ2x4JDw',
  item_ids: ['eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1'],
};

try {
  const response = await client.userItemsAssociate(request);
} catch (error) {
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = UserItemsAssociateRequest(
  user_id='usr_9nSp2KuZ2x4JDw',
  item_ids=['eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1'],
)

response = client.user_items_associate(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::UserItemsAssociateRequest.new(
  {
    user_id: 'usr_9nSp2KuZ2x4JDw',
    item_ids: ['eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1'],
  }
)

response = client.user_items_associate(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`UserItemsAssociateRequest request = new UserItemsAssociateRequest()
  .userId("usr_9nSp2KuZ2x4JDw")
  .itemIds(Arrays.asList("eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1"));

Response<UserItemsAssociateResponse> response = client
  .userItemsAssociate(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewUserItemsAssociateRequest(
  "usr_9nSp2KuZ2x4JDw",
  []string{"eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1"},
)

response, _, err := client.PlaidApi.UserItemsAssociate(ctx).UserItemsAssociateRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.UserItemsAssociateRequest{ItemIDs: []string{"eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1"}}
request.UserID = "usr_9nSp2KuZ2x4JDw"

response, err := client.User.AssociateItems(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/user/items/associate' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "useritemsremove"
    }}>{`/user/items/remove`}</h4>
    <Callout mdxType="Callout">
      <p>{`This endpoint is currently in early availability. To request access, contact your account manager or `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/admin/account-administration/request-product-access"
        }}>{`submit a product access support ticket`}</a>{`.`}</p>
    </Callout>
    <Schema method='POST' route='/user/items/remove' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/user/items/remove' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/user/items/remove \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user_id": "usr_9nSp2KuZ2x4JDw",
  "item_ids": ["eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1"]
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request = {
  user_id: 'usr_9nSp2KuZ2x4JDw',
  item_ids: ['eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1'],
};

try {
  const response = await client.userItemsRemove(request);
} catch (error) {
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = UserItemsRemoveRequest(
  user_id='usr_9nSp2KuZ2x4JDw',
  item_ids=['eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1'],
)

response = client.user_items_remove(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::UserItemsRemoveRequest.new(
  {
    user_id: 'usr_9nSp2KuZ2x4JDw',
    item_ids: ['eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1'],
  }
)

response = client.user_items_remove(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`UserItemsRemoveRequest request = new UserItemsRemoveRequest()
  .userId("usr_9nSp2KuZ2x4JDw")
  .itemIds(Arrays.asList("eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1"));

Response<UserItemsRemoveResponse> response = client
  .userItemsRemove(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewUserItemsRemoveRequest(
  []string{"eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1"},
)
request.SetUserId("usr_9nSp2KuZ2x4JDw")

response, _, err := client.PlaidApi.UserItemsRemove(ctx).UserItemsRemoveRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.UserItemsRemoveRequest{ItemIDs: []string{"eVBnVMp7zdTJLkRNr33Rs6zr27KeyPMNPqfX1"}}
request.UserID = plaid.String("usr_9nSp2KuZ2x4JDw")

response, err := client.User.RemoveItems(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/user/items/remove' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "userproductsterminate"
    }}>{`/user/products/terminate`}</h4>
    <Schema method='POST' route='/user/products/terminate' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/user/products/terminate' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/user/products/terminate \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user_id": "usr_9nSp2KuZ2x4JDw",
  "reason_code": "CONSUMER_ACCOUNT_CLOSED",
  "reason_note": "User closed account with client."
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const request = {
  user_id: 'usr_9nSp2KuZ2x4JDw',
  reason_code: 'CONSUMER_ACCOUNT_CLOSED',
  reason_note: 'User closed account with client.',
};

try {
  const response = await client.userProductsTerminate(request);
} catch (error) {
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = UserProductsTerminateRequest(
  user_id='usr_9nSp2KuZ2x4JDw',
  reason_code=ProductsTerminateReasonCode('CONSUMER_ACCOUNT_CLOSED'),
  reason_note='User closed account with client.',
)

response = client.user_products_terminate(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::UserProductsTerminateRequest.new(
  {
    user_id: 'usr_9nSp2KuZ2x4JDw',
    reason_code: 'CONSUMER_ACCOUNT_CLOSED',
    reason_note: 'User closed account with client.',
  }
)

response = client.user_products_terminate(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`UserProductsTerminateRequest request = new UserProductsTerminateRequest()
  .userId("usr_9nSp2KuZ2x4JDw")
  .reasonCode(ProductsTerminateReasonCode.CONSUMER_ACCOUNT_CLOSED)
  .reasonNote("User closed account with client.");

Response<UserProductsTerminateResponse> response = client
  .userProductsTerminate(request)
  .execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewUserProductsTerminateRequest(
  "usr_9nSp2KuZ2x4JDw",
  plaid.PRODUCTSTERMINATEREASONCODE_CONSUMER_ACCOUNT_CLOSED,
)
request.SetReasonNote("User closed account with client.")

response, _, err := client.PlaidApi.UserProductsTerminate(ctx).UserProductsTerminateRequest(*request).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.UserProductsTerminateRequest{
    UserID:     "usr_9nSp2KuZ2x4JDw",
    ReasonCode: plaid.ProductsTerminateReasonCodeConsumerAccountClosed,
}
request.ReasonNote = plaid.String("User closed account with client.")

response, err := client.User.TerminateProducts(ctx, request)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/user/products/terminate' type='response' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "user-identifiers",
        level: 4,
        title: "User identifiers",
      },
{
        id: "usercreate",
        level: 4,
        title: "/user/create",
      },
{
        id: "userget",
        level: 4,
        title: "/user/get",
      },
{
        id: "userupdate",
        level: 4,
        title: "/user/update",
      },
{
        id: "useritemsget",
        level: 4,
        title: "/user/items/get",
      },
{
        id: "useritemsassociate",
        level: 4,
        title: "/user/items/associate",
      },
{
        id: "useritemsremove",
        level: 4,
        title: "/user/items/remove",
      },
{
        id: "userproductsterminate",
        level: 4,
        title: "/user/products/terminate",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
